import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useId, useState, type ReactNode } from 'react';
import type { ProfileChange, ProfileChangeDetail } from './model';
import { LoadingIndicator, StatusBadge, designTokens } from '../../../design-system';

export type ProfilePendingChangesProps = {
  changes: ProfileChange[];
  label: string;
  canSubmit: boolean;
  canApprove: boolean;
  submittingIds: ReadonlySet<string>;
  approvingIds: ReadonlySet<string>;
  errors: Readonly<Record<string, string>>;
  loadDetails: (change: ProfileChange) => Promise<ProfileChangeDetail>;
  renderDetails: (detail: ProfileChangeDetail) => ReactNode;
  isActionableDetail: (detail: ProfileChangeDetail) => boolean;
  onSubmit: (change: ProfileChange) => void;
  onApprove: (change: ProfileChange) => void;
};

function statusLabel(status: string) {
  if (status === 'DRAFT') return '초안';
  if (status === 'IN_PROGRESS') return '승인 대기';
  return status;
}

export function ProfilePendingChanges({
  changes,
  label,
  canSubmit,
  canApprove,
  submittingIds,
  approvingIds,
  errors,
  loadDetails,
  renderDetails,
  isActionableDetail,
  onSubmit,
  onApprove,
}: ProfilePendingChangesProps) {
  const idPrefix = useId().replaceAll(':', '');
  const [expandedId, setExpandedId] = useState<string>();
  const [details, setDetails] = useState<Record<string, ProfileChangeDetail>>({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});

  if (!changes.length) return null;

  const requestDetails = (change: ProfileChange) => {
    if (loadingIds.has(change.id)) return;
    setLoadingIds((current) => new Set(current).add(change.id));
    setDetailErrors((current) => {
      const next = { ...current };
      delete next[change.id];
      return next;
    });
    void loadDetails(change).then((detail) => {
      setDetails((current) => ({ ...current, [change.id]: detail }));
    }, (reason: unknown) => {
      setDetailErrors((current) => ({
        ...current,
        [change.id]: reason instanceof Error ? reason.message : '변경 요청 상세를 불러오지 못했습니다.',
      }));
    }).finally(() => {
      setLoadingIds((current) => {
        const next = new Set(current);
        next.delete(change.id);
        return next;
      });
    });
  };

  const toggleDetails = (change: ProfileChange) => {
    if (expandedId === change.id) {
      setExpandedId(undefined);
      return;
    }
    setExpandedId(change.id);
    if (!details[change.id]) requestDetails(change);
  };

  return <Box sx={{ px: { xs: `${designTokens.layout.surfacePaddingCompact}px`, sm: `${designTokens.layout.surfacePadding}px` }, pt: 1.5 }}>
    <Stack spacing={1}>
      {changes.map((change, index) => {
        const draft = change.status === 'DRAFT';
        const inProgress = change.status === 'IN_PROGRESS';
        const transitionError = errors[change.id];
        const expanded = expandedId === change.id;
        const detailPanelId = `${idPrefix}-pending-${index}-details`;
        return <Alert key={change.id} severity={transitionError ? 'error' : draft ? 'warning' : 'info'}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'flex-start' } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 720 }}>
                {transitionError ? (draft ? '초안 저장됨, 제출 실패' : `${label} 변경 요청 승인 실패`) : draft ? `${label} 변경 초안이 저장되어 있습니다.` : `${label} 변경 요청을 제출했습니다.`}
              </Typography>
              <Typography variant="caption" component="div">
                <StatusBadge label={statusLabel(change.status)} tone={draft ? 'warning' : inProgress ? 'info' : 'neutral'} />{change.effectiveDate ? ` · 효력일 ${change.effectiveDate}` : ''}
              </Typography>
              {draft && <Typography variant="caption" component="div">{label} 변경 초안은 안전하게 저장되어 있습니다.</Typography>}
              {inProgress && <Typography variant="caption" component="div">현재 원장은 최종 승인 전까지 그대로 유지됩니다.</Typography>}
              {transitionError && <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>{transitionError}</Typography>}
            </Box>
            <Button
              size="small"
              variant="outlined"
              aria-label={`${label} 요청 ${change.id} ${expanded ? '내용 접기' : '내용 보기'}`}
              aria-expanded={expanded}
              aria-controls={expanded ? detailPanelId : undefined}
              onClick={() => toggleDetails(change)}
              sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
            >
              {expanded ? '내용 접기' : '내용 보기'}
            </Button>
          </Stack>
          {expanded && <Box id={detailPanelId} role="region" aria-label={`${label} 요청 ${change.id} 변경 내용`} sx={{ mt: 1.25, p: 1.25, border: 1, borderColor: 'divider', borderRadius: designTokens.radius.surface, bgcolor: 'background.paper' }}>
            {loadingIds.has(change.id) ? <Stack sx={{ alignItems: 'center', minHeight: 48, justifyContent: 'center' }}><LoadingIndicator label="변경 내용을 불러오는 중입니다." /></Stack>
              : detailErrors[change.id] ? <Alert severity="error" action={<Button color="inherit" size="small" aria-label={`${label} 요청 ${change.id} 내용 불러오기 다시 시도`} onClick={() => requestDetails(change)}>다시 시도</Button>}>{detailErrors[change.id]}</Alert>
                : details[change.id] ? <Stack spacing={1.25}>
                  {renderDetails(details[change.id])}
                  <Stack direction="row" spacing={0.75} sx={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {isActionableDetail(details[change.id]) && draft && canSubmit && <Button size="small" variant="outlined" aria-label={`${label} 요청 ${change.id} ${submittingIds.has(change.id) ? '제출 중' : '제출 재시도'}`} onClick={() => onSubmit(change)} disabled={submittingIds.has(change.id)}>
                      {submittingIds.has(change.id) ? '제출 중…' : '제출 재시도'}
                    </Button>}
                    {isActionableDetail(details[change.id]) && inProgress && canApprove && <Button size="small" variant="outlined" aria-label={`${label} 요청 ${change.id} ${approvingIds.has(change.id) ? '승인 중' : '승인하고 반영'}`} onClick={() => onApprove(change)} disabled={approvingIds.has(change.id)}>
                      {approvingIds.has(change.id) ? '승인 중…' : '승인하고 반영'}
                    </Button>}
                  </Stack>
                </Stack> : null}
          </Box>}
        </Alert>;
      })}
    </Stack>
  </Box>;
}
